import {
  useCollections,
  useConnectionStatus,
  useEntitlement,
  usePlatewell,
  useRecipeQuota,
} from '@platewell/query';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AddCollectionDialog } from './components/add-collection-dialog.js';
import { KeyboardShortcutsDialog } from './components/keyboard-shortcuts-dialog.js';
import { LockScreen } from './components/lock-screen.js';
import { PlanBanner } from './components/plan-banner.js';
import { AppSidebar } from './components/nav/app-sidebar.js';
import { MobileTabbar } from './components/nav/mobile-tabbar.js';
import { NavSheet } from './components/nav/nav-sheet.js';
import { SyncStatus } from './components/sync-status.js';
import { ToastProvider } from './components/toast.js';
import { useMediaQuery } from './hooks/use-media-query.js';
import { useSearchShortcut } from './hooks/use-search-shortcut.js';
import { useShortcutsHelpShortcut } from './hooks/use-shortcuts-help-shortcut.js';

export function AppShell() {
  const { store, loading, error, households } = usePlatewell();
  const collections = useCollections();
  const entitlement = useEntitlement();
  const quota = useRecipeQuota();
  const { readOnly } = useConnectionStatus();
  // The width at which the sidebar stops fitting beside the page. Below it the navigation
  // moves to a bottom tab bar, so the two trees are rendered rather than restyled — which
  // also keeps a single navigation landmark on screen at a time.
  const narrow = useMediaQuery('(width <= 900px)');
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const urlTerm = useRouterState({
    select: (state) => (state.location.search as { q?: string }).q ?? '',
  });
  const [term, setTerm] = useState(urlTerm);
  const [addingCollection, setAddingCollection] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [showingShortcuts, setShowingShortcuts] = useState(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const moreButton = useRef<HTMLButtonElement>(null);

  useSearchShortcut(searchInput);
  const openShortcuts = useCallback(() => setShowingShortcuts(true), []);
  const closeShortcuts = useCallback(() => setShowingShortcuts(false), []);
  useShortcutsHelpShortcut(openShortcuts);

  // The button stays visible at the limit and routes to the paywall instead of the editor.
  // A disabled control with no explanation is the worst of the options, the banner already
  // carries the explanation, and keeping it visible is what `primeOfflineShell` in the e2e
  // fixtures waits on. The tab bar's plus makes the same call for itself at phone width.
  // At the household ceiling it still opens the editor: there is nothing to buy, and the
  // banner and the editor's refusal both say so.
  const newRecipeTarget = quota.atLimit ? '/subscribe' : '/recipes/new';

  // Typing owns the input, so it never lags behind the keyboard or fights the debounce. The
  // URL only takes over when it changed from somewhere else — the back link on a recipe, the
  // browser back button, a sidebar link, or a deep link opened cold.
  const pushedTerm = useRef(urlTerm);
  useEffect(() => {
    if (urlTerm !== pushedTerm.current) {
      pushedTerm.current = urlTerm;
      setTerm(urlTerm);
    }
  }, [urlTerm]);

  // Focus came from the More button, so it goes back there rather than to the top of the page.
  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    moreButton.current?.focus();
  }, []);

  // Opening the add dialog closes the sheet in the same breath: `Dialog` restores the body
  // scroll lock it captured on mount, and two of them unmounting out of order would leave
  // the page scrolling behind whichever is still open.
  const openAddCollection = useCallback(() => {
    setSheetOpen(false);
    setAddingCollection(true);
  }, []);

  if (error != null) {
    return (
      <div className="boot">
        <div className="empty">
          <h2>Something went wrong</h2>
          <p className="hint">{error.message}</p>
          <button className="btn" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      </div>
    );
  }

  if (loading || (store == null && households.length > 0)) {
    return <div className="boot">Opening your recipe box…</div>;
  }

  // Rendered instead of the shell, not above it: every mutating call in the store throws
  // while locked, so a half-usable app would be nothing but errors.
  //
  // `/subscribe` is the one exception, and it is load-bearing — it is where the lock
  // screen's own Renew button goes, so short-circuiting it too would leave an owner with
  // a CTA that renders the screen they are already on.
  if (entitlement.plan === 'locked' && pathname !== '/subscribe') return <LockScreen />;

  return (
    <ToastProvider>
      <div className="shell app-shell">
        {!narrow && <AppSidebar onAddCollection={() => setAddingCollection(true)} />}

        <div className="main">
          {/* Above the sticky top bar, as the first child of the column flex `.main`, so it
              pushes the whole page down rather than overlaying it. */}
          <PlanBanner />

          <header className="topbar">
            <input
              ref={searchInput}
              className="search-input"
              type="search"
              placeholder="Search recipes, ingredients, tags…"
              aria-keyshortcuts="Control+K /"
              value={term}
              onChange={(event) => {
                const next = event.target.value;
                setTerm(next);
                pushedTerm.current = next;
                void navigate({ to: '/', search: next === '' ? {} : { q: next } });
              }}
            />
            <span className="spacer" />
            <SyncStatus />
            {!readOnly && !narrow && (
              <button
                className="btn primary"
                onClick={() => void navigate({ to: newRecipeTarget })}
              >
                New recipe
              </button>
            )}
          </header>

          <main className="content">
            {store == null ? (
              <div className="empty">
                <h2>No household yet</h2>
                <p className="hint">Create one from Profile to start adding recipes.</p>
              </div>
            ) : (
              <Outlet />
            )}
          </main>
        </div>

        {narrow && (
          <div className="mobile-chrome">
            <MobileTabbar onMore={() => setSheetOpen(true)} moreRef={moreButton} />
          </div>
        )}

        {narrow && sheetOpen && (
          <NavSheet onClose={closeSheet} onAddCollection={openAddCollection} />
        )}

        {addingCollection && store != null && (
          <AddCollectionDialog
            existingNames={collections.map((collection) => collection.name)}
            onClose={() => setAddingCollection(false)}
            onCreate={async (name) => {
              const id = await store.createCollection({ name });
              setAddingCollection(false);
              await navigate({ to: '/collections/$collectionId', params: { collectionId: id } });
            }}
          />
        )}

        {showingShortcuts && <KeyboardShortcutsDialog onClose={closeShortcuts} />}
      </div>
    </ToastProvider>
  );
}
